// src/components/Holdings.jsx
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Box,
  Typography,
  useTheme } from "@mui/material";
import api,
  { fetchHoldings,
  fetchHoldingsDates,
  fetchHoldingsView,
  saveHoldings,
  deleteHoldings } from "../api";
import { formatDate } from "../utils";
import {
  ConfirmationDialog,
  PamGrid,
  useNotification,
  deepClone,
  HistoryModal,
  parseFileData,
  buildHeaderMappingsFromAliases,
  createColumnNormalizer,
  normalizeRowKeys,
  mergeRows,
  isPermissionDenied,
  getPermissionDeniedMessage,
  GridConfigMenu,
  useGridConfigs,
  notifyPermissionError,
  getWriteErrorMessage,
  isVersionConflict,
  loadedModifiedDate,
  shouldReloadAfterWriteError,
  MODIFIED_DATE_FIELD,
} from '@1823-partners/core';
import HoldingsHistory from "./HoldingsHistory";

// Header alias map for holdings file uploads
const HOLDINGS_HEADER_ALIASES = {
  CUSIP: ['CUSIP'],
  SECURITY: ['SECURITY', 'NAME', 'SECURITY_NAME', 'SECURITYNAME', 'Name'],
  ISSUER_TRUST: ['ISSUER_TRUST', 'ISSUERTRUST', 'ISSUER', 'TRUST', 'Issuer Trust'],
  WAL_CURR: ['WAL', 'WAL_CURR', 'WALCURR', 'WEIGHTED_AVERAGE_LIFE', 'WEIGHTEDAVERAGELIFE'],
  YIELD_CURR: ['YIELD', 'YIELD_CURR', 'YIELDCURR', 'BOOK_YIELD', 'BOOKYIELD', 'BOOK_YIELDCURR', 'YIELD_CURRENT', 'Book Yield', 'Yield'],
  CE_CURR: ['CE', 'CE_CURR', 'CECURR', 'CREDIT_ENHANCEMENT', 'CREDITENHANCEMENT', 'CREDIT_ENHANCEMENT_CURR', 'CE_CURRENT', 'Credit Enhancement'],
  THICK_CURR: ['THICK', 'THICK_CURR', 'THICKCURR', 'THICKNESS', 'THICKNESS_CURR', 'THICKNESS_CURRENT', 'Thickness'],
  NOTES: ['NOTES', 'NOTE', 'COMMENTS', 'COMMENT', 'Notes'],
};

const HOLDINGS_HEADER_MAPPINGS = buildHeaderMappingsFromAliases(HOLDINGS_HEADER_ALIASES);
const holdingsColumnNormalizer = createColumnNormalizer(HOLDINGS_HEADER_MAPPINGS);

// Fields that can be uploaded (grid field names)
const HOLDINGS_UPLOADABLE_FIELDS = ['SECURITY', 'ISSUER_TRUST', 'WAL_CURR', 'YIELD_CURR', 'CE_CURR', 'THICK_CURR', 'NOTES'];

// updatedRows without the given CUSIPs' pending edits.
const withoutCusips = (changes, cusips) => {
  const next = { ...changes };
  cusips.forEach((cusip) => { delete next[cusip]; });
  return next;
};

const Holdings = ({ userId }) => {
  const [holdingsData, setHoldingsData] = useState([]);
  const [originalHoldingsData, setOriginalHoldingsData] = useState([]); // Store original data from API
  const [holdingsQuickFilter, setHoldingsQuickFilter] = useState('');
  const [holdingsError, setHoldingsError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [selectedHoldingForHistory, setSelectedHoldingForHistory] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [holdingToDelete, setHoldingToDelete] = useState(null);
  const [updatedRows, setUpdatedRows] = useState({}); // Structure: {cusip: {field: value}} - only changed fields
  const [availableDates, setAvailableDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState('');
  const [isLoadingDates, setIsLoadingDates] = useState(false);
  const [showOnlyChanges, setShowOnlyChanges] = useState(false);
  const [selectedRows, setSelectedRows] = useState(new Set()); // Track selected rows for bulk operations
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const gridRef = useRef(null);
  const isPastingRef = useRef(false);
  const pendingUpdatedRowsRef = useRef(null);
  const updatedRowsRef = useRef(updatedRows);
  updatedRowsRef.current = updatedRows;
  // The /holdings-entries rows of the selected date as last loaded, by CUSIP.
  // A save or delete sends the MODIFIED_DATE of this row (null when the CUSIP
  // has no stored HOLDINGS row, e.g. a holdings_view-only row) — never the
  // view's, and never one re-fetched at save time.
  const loadedEntriesRef = useRef({});

  const theme = useTheme();

  const isGridEditable = true;

  // Actions are disabled only during loading/saving/deleting operations
  const areActionsDisabled = useMemo(() => {
    return isLoading || isSaving || isDeleting;
  }, [isLoading, isSaving, isDeleting]);

  const { showNotification } = useNotification();

  // Load available dates function
  const loadAvailableDates = useCallback(async () => {
    try {
      setIsLoadingDates(true);
      const response = await fetchHoldingsDates();
      const rawDates = response.data;

      // Format the dates using the formatDate utility function (same as Overrides)
      const formattedDates = rawDates.map(date => formatDate(date));
      setAvailableDates(formattedDates);

      // Set the first date as default if no date is selected
      if (formattedDates.length > 0 && !selectedDate) {
        setSelectedDate(formattedDates[0]);
      }
    } catch (error) {
      console.error("Error fetching available dates:", error);
      if (isPermissionDenied(error)) {
        setHoldingsError(getPermissionDeniedMessage(error));
      } else {
        setHoldingsError("Failed to load available dates. Please try again.");
      }
    } finally {
      setIsLoadingDates(false);
    }
  }, [selectedDate]);

  // Load holdings function. `keepEdits` (updatedRows after a failed write) is
  // re-applied over the fresh rows so the user can re-apply their changes.
  // Those rows keep the entries row (and MODIFIED_DATE) the edits were based
  // on, unless `adoptServerModifiedDates` (a save 409, where every edited row
  // was in the rejected batch): then saving again is the user's re-apply.
  const loadHoldings = useCallback(async ({ keepEdits = null, adoptServerModifiedDates = false } = {}) => {
    if (!selectedDate) return;

    try {
      setIsLoading(true);
      setHoldingsError(null);

      // Get previous date from available dates (the asofdt one smaller than selectedDate)
      const sortedDates = availableDates.sort((a, b) => new Date(b) - new Date(a));
      const currentDateIndex = sortedDates.indexOf(selectedDate);
      const previousDate = currentDateIndex < sortedDates.length - 1 ? sortedDates[currentDateIndex + 1] : null;


      // Fetch data from both endpoints for current and previous dates
      const apiCalls = [
        fetchHoldings(selectedDate),
        fetchHoldingsView(selectedDate)
      ];

      // Only fetch previous data if a previous date exists
      if (previousDate) {
        apiCalls.push(
          fetchHoldings(previousDate),
          fetchHoldingsView(previousDate)
        );
      }

      const responses = await Promise.all(apiCalls);
      const [currentHoldingsResponse, currentHoldingsViewResponse, previousHoldingsResponse, previousHoldingsViewResponse] = responses;

      // Combine data from both endpoints
      const currentHoldingsData = currentHoldingsResponse.data || [];
      const currentHoldingsViewData = currentHoldingsViewResponse.data || [];
      const previousHoldingsData = previousHoldingsResponse?.data || [];
      const previousHoldingsViewData = previousHoldingsViewResponse?.data || [];
      const freshEntries = Object.fromEntries(
        currentHoldingsData.map((holding) => [holding.CUSIP, holding])
      );
      if (keepEdits && !adoptServerModifiedDates) {
        const previousEntries = loadedEntriesRef.current;
        Object.keys(keepEdits).forEach((cusip) => {
          if (previousEntries[cusip]) {
            freshEntries[cusip] = previousEntries[cusip];
          } else {
            delete freshEntries[cusip];
          }
        });
      }
      loadedEntriesRef.current = freshEntries;

      const cusips = [...new Set([...currentHoldingsData.map(holding => holding.CUSIP), ...currentHoldingsViewData.map(holding => holding.CUSIP)])];
      cusips.sort();

      const combinedData = cusips.map(cusip => {
        const currentHolding = currentHoldingsData.find(holding => holding.CUSIP === cusip);
        const currentHoldingView = currentHoldingsViewData.find(holding => holding.CUSIP === cusip);
        const previousHolding = previousHoldingsData.find(holding => holding.CUSIP === cusip);
        const previousHoldingView = previousHoldingsViewData.find(holding => holding.CUSIP === cusip);

        // Create merged object
        const mergedHolding = {};

        // Define all expected grid fields and their mappings
        const allGridFields = {
          'CUSIP': { viewField: 'CUSIP', entriesField: 'CUSIP' },
          'SECURITY': { viewField: 'SECURITY', entriesField: 'SECURITY' },
          'ISSUER_TRUST': { viewField: 'ISSUER_TRUST', entriesField: 'ISSUER_TRUST' },
          'CW_ASSET_CLASS': { viewField: 'CW_ASSET_CLASS', entriesField: 'CW_ASSET_CLASS' },
          'TEAM': { viewField: 'TEAM', entriesField: 'TEAM' },
          'FINAL_RATING': { viewField: 'FINAL_RATING', entriesField: 'FINAL_RATING' },
          'BROAD_ASSET_CLASS': { viewField: 'BROAD_ASSET_CLASS', entriesField: 'BROAD_ASSET_CLASS' },
          'SUBTOTAL_TAG': { viewField: 'SUBTOTAL_TAG', entriesField: 'SUBTOTAL_TAG' },
          'FINAL_GROUPING': { viewField: 'FINAL_GROUPING', entriesField: 'FINAL_GROUPING' },
          'BOOK_VALUE': { viewField: 'BOOK_VALUE', entriesField: 'BOOK_VALUE' },
          'WAL_CURR': { viewField: 'WAL', entriesField: 'WAL' },
          'YIELD_CURR': { viewField: 'YIELD', entriesField: 'BOOK_YIELD' },
          'CE_CURR': { viewField: 'CE', entriesField: 'CREDIT_ENHANCEMENT' },
          'THICK_CURR': { viewField: 'THICK', entriesField: 'THICKNESS' },
          'RISK_RATING': { viewField: 'RISK_RATING', entriesField: 'RISK_RATING' },
          'CLASS_RATING': { viewField: 'CLASS_RATING', entriesField: 'CLASS_RATING' },
          'SPONSOR_MANAGER': { viewField: 'SPONSOR_MANAGER', entriesField: 'SPONSOR_MANAGER' },
          'NOTES': { viewField: 'NOTES', entriesField: 'NOTES' },
          'MODIFIED_BY': { viewField: 'MODIFIED_BY', entriesField: 'MODIFIED_BY' },
          'MODIFIED_DATE': { viewField: 'MODIFIED_DATE', entriesField: 'MODIFIED_DATE' },
          'ASOFDT': { viewField: 'ASOFDT', entriesField: 'ASOFDT' },
          'STATUS': { viewField: 'STATUS', entriesField: 'STATUS' }
        };

        // Process all expected grid fields
        Object.keys(allGridFields).forEach(gridField => {
          const mapping = allGridFields[gridField];
          const entriesValue = currentHolding?.[mapping.entriesField];
          const viewValue = currentHoldingView?.[mapping.viewField];

          // Use entries data if it exists and is not null/undefined/empty, otherwise use view data
          if (entriesValue !== null && entriesValue !== undefined && entriesValue !== '') {
            mergedHolding[gridField] = entriesValue;
          } else if (viewValue !== null && viewValue !== undefined && viewValue !== '') {
            mergedHolding[gridField] = viewValue;
          } else {
            // If both are empty, set to empty string for consistency
            mergedHolding[gridField] = '';
          }
        });

        const prevGridFields = {
          'WAL_PREV': { viewField: 'WAL', entriesField: 'WAL' },
          'YIELD_PREV': { viewField: 'YIELD', entriesField: 'BOOK_YIELD' },
          'CE_PREV': { viewField: 'CE', entriesField: 'CREDIT_ENHANCEMENT' },
          'THICK_PREV': { viewField: 'THICK', entriesField: 'THICKNESS' },
          // Add previous month data for comparison fields
          'ISSUER_TRUST_PREV': { viewField: 'ISSUER_TRUST', entriesField: 'ISSUER_TRUST' },
          'SECURITY_PREV': { viewField: 'SECURITY', entriesField: 'SECURITY' },
          'CW_ASSET_CLASS_PREV': { viewField: 'CW_ASSET_CLASS', entriesField: 'CW_ASSET_CLASS' },
          'TEAM_PREV': { viewField: 'TEAM', entriesField: 'TEAM' },
          'FINAL_RATING_PREV': { viewField: 'FINAL_RATING', entriesField: 'FINAL_RATING' },
          'BROAD_ASSET_CLASS_PREV': { viewField: 'BROAD_ASSET_CLASS', entriesField: 'BROAD_ASSET_CLASS' },
          'SUBTOTAL_TAG_PREV': { viewField: 'SUBTOTAL_TAG', entriesField: 'SUBTOTAL_TAG' },
          'FINAL_GROUPING_PREV': { viewField: 'FINAL_GROUPING', entriesField: 'FINAL_GROUPING' }
        };

        Object.keys(prevGridFields).forEach(prevField => {
          const mapping = prevGridFields[prevField];
          const entriesValue = previousHolding?.[mapping.entriesField];
          const viewValue = previousHoldingView?.[mapping.viewField];

          if (entriesValue !== null && entriesValue !== undefined && entriesValue !== '') {
            mergedHolding[prevField] = entriesValue;
          } else if (viewValue !== null && viewValue !== undefined && viewValue !== '') {
            mergedHolding[prevField] = viewValue;
          } else {
            // If both are empty, set to empty string for consistency
            mergedHolding[prevField] = '';
          }
        });

        // Set ASOFDT to the selected date
        mergedHolding.ASOFDT = selectedDate;

        return mergedHolding;
      });

      // Filter to only include rows STATUS!='Deleted' or STATUS is null
      const activeHoldingsData = combinedData.filter(holding => holding.STATUS !== 'Deleted' || holding.STATUS === null);

      // Store original data (deep copy) before any edits
      setOriginalHoldingsData(deepClone(activeHoldingsData));

      if (keepEdits && Object.keys(keepEdits).length > 0) {
        // Unsaved edits stay visible (and tracked) over the fresh rows.
        activeHoldingsData.forEach((row) => {
          const changes = keepEdits[row.CUSIP];
          if (changes) {
            Object.keys(changes)
              .filter((field) => field !== MODIFIED_DATE_FIELD && field !== 'MODIFIED_BY')
              .forEach((field) => { row[field] = changes[field]; });
          }
        });
        setHoldingsData(activeHoldingsData);
        updatedRowsRef.current = keepEdits;
        setUpdatedRows(keepEdits);
      } else {
        setHoldingsData(activeHoldingsData);
        // Reset change tracking when loading fresh data
        setUpdatedRows({});
      }

      // Auto-size columns after data is loaded
      setTimeout(() => {
        if (gridRef.current && gridRef.current.api) {
          // Auto-size all columns to their content (including headers)
          gridRef.current.api.autoSizeAllColumns();
          // Then fit columns to available width
          gridRef.current.api.sizeColumnsToFit();
        }
      }, 100); // Small delay to ensure grid is fully rendered
    } catch (error) {
      console.error("Error fetching holdings:", error);
      if (isPermissionDenied(error)) {
        setHoldingsError(getPermissionDeniedMessage(error));
      } else {
        setHoldingsError("Failed to load holdings. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  }, [selectedDate, availableDates]);

  useEffect(() => {
    loadAvailableDates();
  }, [loadAvailableDates]);

  useEffect(() => {
    if (selectedDate) {
      loadHoldings();
    }
  }, [loadHoldings, selectedDate]);

  // Clear selection when date changes
  useEffect(() => {
    setSelectedRows(new Set());
  }, [selectedDate]);

  // Holdings action handlers
  const handleHoldingHistory = useCallback((holdingData) => {
    setSelectedHoldingForHistory(holdingData);
    setHistoryModalOpen(true);
  }, []);

  // Helper function to filter row data to only include fields needed for delete endpoint
  const filterRowForDelete = useCallback((row) => {
    // Mapping for _CURR fields to their proper names for the delete endpoint
    const currFieldMapping = {
      'WAL_CURR': 'WAL',
      'YIELD_CURR': 'BOOK_YIELD',
      'CE_CURR': 'CREDIT_ENHANCEMENT',
      'THICK_CURR': 'THICKNESS'
    };

    // Numeric fields that should be converted to null if empty
    const numericFields = ['WAL', 'BOOK_YIELD', 'CREDIT_ENHANCEMENT', 'THICKNESS'];

    // Helper function to check if a value is empty
    const isEmpty = (value) => {
      return value === null || value === undefined || value === '' ||
        (typeof value === 'string' && value.trim() === '');
    };

    // Fields that should be sent to the delete endpoint
    const allowedFields = [
      'CUSIP',
      'ISSUER_TRUST',
      'WAL',
      'BOOK_YIELD',
      'CREDIT_ENHANCEMENT',
      'THICKNESS',
      'NOTES',
      'MODIFIED_BY',
      'MODIFIED_DATE',
      'ASOFDT',
      'SECURITY'
    ];

    const filteredRow = {};

    // Process each allowed field
    allowedFields.forEach(field => {
      // First check if there's a _CURR version that maps to this field
      const currFieldKey = Object.keys(currFieldMapping).find(
        key => currFieldMapping[key] === field
      );

      if (currFieldKey && currFieldKey in row) {
        // Use the _CURR field value
        filteredRow[field] = row[currFieldKey];
      } else if (field in row) {
        // Use the field directly if it exists
        filteredRow[field] = row[field];
      }
    });

    // Convert empty numeric fields to null (same as save method)
    numericFields.forEach(field => {
      if (field in filteredRow && isEmpty(filteredRow[field])) {
        filteredRow[field] = null;
      }
    });

    // MODIFIED_DATE is the loaded HOLDINGS row's (null for a view-only row):
    // the tombstone lands only if that row is still the latest. The writer
    // stamps MODIFIED_BY / MODIFIED_DATE itself.
    filteredRow.MODIFIED_BY = userId;
    filteredRow[MODIFIED_DATE_FIELD] = loadedModifiedDate(loadedEntriesRef.current[row.CUSIP]);

    // Ensure ASOFDT is set to selectedDate
    filteredRow.ASOFDT = selectedDate;

    return filteredRow;
  }, [userId, selectedDate]);

  const handleDeleteHolding = useCallback((holdingData) => {
    setHoldingToDelete(holdingData);
    setDeleteDialogOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!holdingToDelete) return;

    try {
      setIsDeleting(true);
      // Filter row to only include required fields
      const filteredRow = filterRowForDelete(holdingToDelete);

      // Call the delete API with a single-item array
      await deleteHoldings(userId, [filteredRow]);

      // Refresh the data to remove the deleted holding
      await loadHoldings();

      // Show success message
    } catch (error) {
      console.error('Error deleting holding:', error);
      if (!notifyPermissionError(showNotification, error)) {
        showNotification(getWriteErrorMessage(error, 'Failed to delete holding. Please try again.'), 'error');
      }
      if (shouldReloadAfterWriteError(error)) {
        // The conflict is about the deleted row: reload it as saved now (its
        // unsaved edits are dropped; it was being deleted), so deleting again
        // sends the latest row's MODIFIED_DATE. Other edits keep their base.
        await loadHoldings({ keepEdits: withoutCusips(updatedRowsRef.current, [holdingToDelete.CUSIP]) });
      }
    } finally {
      setIsDeleting(false);
      setHoldingToDelete(null);
    }
  }, [holdingToDelete, userId, loadHoldings, filterRowForDelete, showNotification]);

  const handleCloseDeleteDialog = useCallback(() => {
    setDeleteDialogOpen(false);
    setHoldingToDelete(null);
  }, []);

  const handleBulkDelete = useCallback(() => {
    if (selectedRows.size === 0) return;
    setBulkDeleteDialogOpen(true);
  }, [selectedRows]);

  const handleConfirmBulkDelete = useCallback(async () => {
    if (selectedRows.size === 0) return;

    try {
      setIsDeleting(true);
      const rowsToDelete = holdingsData.filter(row =>
        selectedRows.has(row.CUSIP)
      );

      if (rowsToDelete.length === 0) {
        showNotification('No holdings selected for deletion', 'warning');
        return;
      }

      // Filter rows to only include required fields
      const filteredRows = rowsToDelete.map(row => filterRowForDelete(row));

      // Call the delete API
      await deleteHoldings(userId, filteredRows);

      // Clear selection
      setSelectedRows(new Set());

      // Refresh data after deletion
      await loadHoldings();

      showNotification(
        `Successfully deleted ${rowsToDelete.length} holding${rowsToDelete.length === 1 ? '' : 's'}`,
        'success'
      );
    } catch (error) {
      console.error('Error during bulk delete:', error);
      if (!notifyPermissionError(showNotification, error)) {
        // The batch is all-or-nothing: nothing was deleted unless the text says otherwise.
        showNotification(getWriteErrorMessage(error, 'Failed to delete the selected holdings'), 'error');
      }
      if (shouldReloadAfterWriteError(error)) {
        // As for a single delete: the selected rows reload as saved now.
        await loadHoldings({ keepEdits: withoutCusips(updatedRowsRef.current, [...selectedRows]) });
      }
    } finally {
      setIsDeleting(false);
      setBulkDeleteDialogOpen(false);
    }
  }, [selectedRows, holdingsData, userId, loadHoldings, showNotification, filterRowForDelete]);

  const handleCloseBulkDeleteDialog = useCallback(() => {
    setBulkDeleteDialogOpen(false);
  }, []);

  const handleCellValueChanged = useCallback((params) => {
    const { data, colDef, newValue } = params;

    const cusip = data.CUSIP || data.id;

    // Get original value from originalHoldingsData
    const originalRow = originalHoldingsData.find(row => row.CUSIP === cusip);
    const originalValue = originalRow?.[colDef.field];

    // Check if value actually changed from original
    const hasChanged = (() => {
      // Handle null/undefined/empty comparisons
      const normalizeValue = (value) => {
        if (value === null || value === undefined || value === '') return null;
        return value;
      };

      const normalizedOld = normalizeValue(originalValue);
      const normalizedNew = normalizeValue(newValue);

      if (normalizedOld === null && normalizedNew === null) return false;
      if (normalizedOld === null || normalizedNew === null) return true;
      if (typeof normalizedOld === 'number' && typeof normalizedNew === 'number') {
        return Math.abs(normalizedOld - normalizedNew) > 0.0001;
      }
      return String(normalizedOld).trim() !== String(normalizedNew).trim();
    })();

    const nextTracking = (prev) => {
      const existingChanges = prev[cusip] || {};

      if (hasChanged) {
        // Add or update the changed field
        return {
          ...prev,
          [cusip]: {
            ...existingChanges,
            [colDef.field]: newValue
          }
        };
      }

      // Value matches original, remove from changes if it was there
      const { [colDef.field]: removed, ...restChanges } = existingChanges;

      // If no changes remain for this CUSIP, remove it entirely
      if (Object.keys(restChanges).length === 0) {
        const { [cusip]: removedCusip, ...rest } = prev;
        return rest;
      }

      return {
        ...prev,
        [cusip]: restChanges
      };
    };

    // During paste, AG Grid mutates row objects in place. Cloning React state
    // (and rebuilding columnDefs via updatedRows) mid-paste interrupts the
    // remaining clipboard cells, so batch tracking until onPasteEnd.
    if (isPastingRef.current) {
      if (pendingUpdatedRowsRef.current === null) {
        pendingUpdatedRowsRef.current = Object.fromEntries(
          Object.entries(updatedRowsRef.current).map(([key, value]) => [key, { ...value }])
        );
      }
      pendingUpdatedRowsRef.current = nextTracking(pendingUpdatedRowsRef.current);
      return;
    }

    // AG Grid already mutated `data` in place. Keep that object identity so
    // Tab/Shift-Tab/typed edits are not interrupted by a holdingsData clone,
    // and keep getCellClass on a ref so columnDefs stay stable.
    const next = nextTracking(updatedRowsRef.current);
    updatedRowsRef.current = next;
    setUpdatedRows(next);

    const { api } = params;
    if (api?.refreshCells) {
      const rowNode = api.getRowNode?.(cusip);
      api.refreshCells({
        rowNodes: rowNode ? [rowNode] : undefined,
        columns: [colDef.field],
        force: true
      });
    }
  }, [originalHoldingsData]);

  // Auto-size columns when data changes
  const handleGridReady = useCallback(() => {
    if (gridRef.current && gridRef.current.api) {
      setTimeout(() => {
        // Check if ref is still valid before accessing API
        if (gridRef.current && gridRef.current.api) {
          // Auto-size all columns to their content (including headers)
          gridRef.current.api.autoSizeAllColumns();
          // Then fit columns to available width
          gridRef.current.api.sizeColumnsToFit();
        }
      }, 100);
    }
  }, []);

  // Auto-size columns when quick filter changes
  useEffect(() => {
    if (gridRef.current && gridRef.current.api) {
      setTimeout(() => {
        // Check if ref is still valid before accessing API
        if (gridRef.current && gridRef.current.api) {
          // Auto-size all columns to their content (including headers)
          gridRef.current.api.autoSizeAllColumns();
          // Then fit columns to available width
          gridRef.current.api.sizeColumnsToFit();
        }
      }, 100);
    }
  }, [holdingsQuickFilter]);

  // Copy/paste functionality handlers
  const onPasteStart = useCallback(() => {
    isPastingRef.current = true;
    pendingUpdatedRowsRef.current = null;
  }, []);

  const onPasteEnd = useCallback(() => {
    isPastingRef.current = false;
    const pending = pendingUpdatedRowsRef.current;
    pendingUpdatedRowsRef.current = null;
    if (pending) {
      updatedRowsRef.current = pending;
      setUpdatedRows(pending);
      // Snapshot in-place AG Grid mutations into React state after paste completes
      setHoldingsData(prevData => prevData.map(row => ({ ...row })));
    }
    showNotification('Data pasted successfully', 'success');
  }, [showNotification]);

  // Toast handler for Ctrl+C — PamGrid does the actual copy and calls this
  // with { scope: 'range' | 'cell' }.
  const handleCopy = useCallback(({ scope }) => {
    showNotification(
      scope === 'range' ? 'Selected cells copied to clipboard' : 'Cell copied to clipboard',
      'success'
    );
  }, [showNotification]);

  // Ctrl+X (cut) is app-specific — PamGrid doesn't manage it. Ctrl+C and
  // Ctrl+V are handled by PamGrid via the onCopy / allowPaste props.
  const onCellKeyDown = useCallback((params) => {
    const { event, api } = params;

    // If the user is typing/editing inside an input/editor, don't hijack shortcuts
    const target = event?.target;
    const isTextInputTarget =
      target &&
      (target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable);
    if (isTextInputTarget) return;

    const isCtrlOrMeta = event.ctrlKey || event.metaKey;
    const key = (event.key || '').toLowerCase();
    const canModifyGrid = isGridEditable && !isLoading && !isSaving && !isDeleting;

    // Handle Ctrl/Cmd+X for cutting (copy + clear selected editable cells)
    if (isCtrlOrMeta && key === 'x') {
      event.preventDefault();
      if (!canModifyGrid) return;

      if (typeof api.cutSelectedRangeToClipboard === 'function') {
        api.cutSelectedRangeToClipboard();
        showNotification('Selected cells cut to clipboard', 'success');
        return;
      }

      api.copySelectedRangeToClipboard();

      const cellRanges = api.getCellRanges();
      const focusedCell = api.getFocusedCell();

      const clearCell = (rowNode, column) => {
        if (!rowNode || !column) return;
        const colId = column.getColId();
        const colDef = column.getColDef ? column.getColDef() : undefined;
        const editable =
          typeof colDef?.editable === 'function'
            ? colDef.editable({ node: rowNode, data: rowNode.data, colDef, column })
            : !!colDef?.editable;
        if (!editable) return;
        rowNode.setDataValue(colId, '');
      };

      if (cellRanges && cellRanges.length > 0) {
        cellRanges.forEach(range => {
          const startIndex = Math.min(range.startRow.rowIndex, range.endRow.rowIndex);
          const endIndex = Math.max(range.startRow.rowIndex, range.endRow.rowIndex);
          for (let rowIndex = startIndex; rowIndex <= endIndex; rowIndex += 1) {
            const rowNode = api.getDisplayedRowAtIndex(rowIndex);
            if (!rowNode) continue;
            range.columns.forEach(col => clearCell(rowNode, col));
          }
        });
      } else if (focusedCell) {
        const rowNode = api.getDisplayedRowAtIndex(focusedCell.rowIndex);
        clearCell(rowNode, focusedCell.column);
      }

      showNotification('Selected cells cut to clipboard', 'success');
    }
  }, [showNotification, isGridEditable, isLoading, isSaving, isDeleting]);

  // Function to determine cell classes based on update status and month comparison.
  // Read tracking from a ref so columnDefs stay stable across sequential edits —
  // rebuilding them mid-edit destroys AG Grid's editor.
  const getCellClass = useCallback((params) => {
    const { data, colDef } = params;
    const field = colDef.field;

    // Skip non-data columns and metadata columns that shouldn't be highlighted
    if (!data || !field || ['actions', 'select', 'MODIFIED_BY', 'MODIFIED_DATE'].includes(field)) {
      return '';
    }

    const cusip = data.CUSIP;
    const updatedRow = updatedRowsRef.current[cusip];

    // Check for orange highlighting (user updates)
    if (updatedRow && updatedRow.hasOwnProperty(field)) {
      return 'ag-cell-updated';
    }

    // Check for red highlighting (month comparison)
    const fieldsToCompare = ['SECURITY', 'ISSUER_TRUST', 'CW_ASSET_CLASS', 'TEAM', 'FINAL_RATING', 'BROAD_ASSET_CLASS', 'SUBTOTAL_TAG', 'FINAL_GROUPING'];
    if (fieldsToCompare.includes(field)) {
      const currentValue = data[field];
      const prevValue = data[`${field}_PREV`];

      // Compare current value with previous value
      if ((currentValue || '') !== (prevValue || '')) {
        return 'ag-cell-month-diff';
      }
    }

    if (field === 'WAL_CURR') {
      const currentValue = data[field];
      const prevValue = data['WAL_PREV'];
      if (Math.abs((currentValue || 0) - (prevValue || 0)) >= .5) {
        return 'ag-cell-month-diff';
      }
    } else if (field === 'YIELD_CURR') {
      const currentValue = data[field];
      const prevValue = data['YIELD_PREV'];
      if (Math.abs((currentValue || 0) - (prevValue || 0)) >= 1) {
        return 'ag-cell-month-diff';
      }
    }

    return '';
  }, []);

  // Function to check if a row has month comparison differences
  const hasMonthDifferences = useCallback((row) => {
    const fieldsToCompare = ['SECURITY', 'ISSUER_TRUST', 'CW_ASSET_CLASS', 'TEAM', 'FINAL_RATING', 'BROAD_ASSET_CLASS', 'SUBTOTAL_TAG', 'FINAL_GROUPING'];

    // Check text fields
    for (const field of fieldsToCompare) {
      const currentValue = row[field];
      const prevValue = row[`${field}_PREV`];
      if ((currentValue || '') !== (prevValue || '')) {
        return true;
      }
    }

    // Check WAL_CURR
    const walCurrent = row['WAL_CURR'];
    const walPrev = row['WAL_PREV'];
    if (Math.abs((walCurrent || 0) - (walPrev || 0)) >= 0.5) {
      return true;
    }

    // Check YIELD_CURR
    const yieldCurrent = row['YIELD_CURR'];
    const yieldPrev = row['YIELD_PREV'];
    if (Math.abs((yieldCurrent || 0) - (yieldPrev || 0)) >= 1) {
      return true;
    }

    return false;
  }, []);

  // Filter data based on showOnlyChanges state
  const filteredHoldingsData = useMemo(() => {
    if (!showOnlyChanges) {
      return holdingsData;
    }
    return holdingsData.filter(hasMonthDifferences);
  }, [holdingsData, showOnlyChanges, hasMonthDifferences]);

  const handleSaveHoldings = useCallback(async () => {
    try {
      setIsSaving(true);

      // Get all updated CUSIPs
      const updatedCusips = Object.keys(updatedRows);
      if (updatedCusips.length === 0) {
        showNotification('No changes to save', 'warning');
        return;
      }

      // The entries rows as loaded (not re-fetched): each saved row is based
      // on, and sends the MODIFIED_DATE of, the row the user was looking at.
      const loadedEntries = loadedEntriesRef.current;

      // Field mappings from grid fields to entries API fields (no view fields)
      const fieldMappings = {
        'CUSIP': { entriesField: 'CUSIP' },
        'SECURITY': { entriesField: 'SECURITY' },
        'ISSUER_TRUST': { entriesField: 'ISSUER_TRUST' },
        'WAL_CURR': { entriesField: 'WAL' },
        'YIELD_CURR': { entriesField: 'BOOK_YIELD' },
        'CE_CURR': { entriesField: 'CREDIT_ENHANCEMENT' },
        'THICK_CURR': { entriesField: 'THICKNESS' },
        'RISK_RATING': { entriesField: 'RISK_RATING' },
        'CLASS_RATING': { entriesField: 'CLASS_RATING' },
        'SPONSOR_MANAGER': { entriesField: 'SPONSOR_MANAGER' },
        'NOTES': { entriesField: 'NOTES' }
      };

      // Numeric fields that should be converted to null if empty
      const numericFields = ['WAL', 'BOOK_YIELD', 'CREDIT_ENHANCEMENT', 'THICKNESS', 'RISK_RATING'];

      // Helper function to check if a value is empty
      const isEmpty = (value) => {
        return value === null || value === undefined || value === '' ||
          (typeof value === 'string' && value.trim() === '');
      };

      // Columns that should not be sent to the save endpoint
      const columnsToExclude = [
        'BOOK_VALUE', 'BROAD_ASSET_CLASS', 'CW_ASSET_CLASS',
        'FINAL_GROUPING', 'SUBTOTAL_TAG', 'TEAM', 'FINAL_RATING'
      ];

      // Define all required fields that must be included in the save payload (entries format)
      // These fields will be set to null if they don't have a value from holdings table or an update
      const requiredFields = [
        'CUSIP',
        'SECURITY',
        'ISSUER_TRUST',
        'WAL',
        'BOOK_YIELD',
        'CREDIT_ENHANCEMENT',
        'THICKNESS',
        'RISK_RATING',
        'CLASS_RATING',
        'SPONSOR_MANAGER',
        'NOTES',
        'MODIFIED_BY',
        'MODIFIED_DATE',
        'ASOFDT',
        'STATUS'
      ];

      // Build save data: overlay only changed fields on original data (entries only, no view)
      const completeUpdatedData = updatedCusips.map(cusip => {
        // Find original data from entries API only
        const originalHolding = loadedEntries[cusip];

        // Get changed fields for this CUSIP (in grid format)
        const changedFields = updatedRows[cusip] || {};

        // Start with ALL fields from original entries data (spread all original fields)
        const saveRow = originalHolding ? { ...originalHolding } : {};

        // Overlay changed fields, converting from grid format to entries format
        Object.keys(changedFields).forEach(gridField => {
          if (gridField === 'MODIFIED_BY' || gridField === 'MODIFIED_DATE' || gridField === 'ASOFDT') {
            return; // Skip these, we'll set them explicitly
          }

          // Convert grid field to entries field if needed
          const mapping = fieldMappings[gridField];
          if (mapping) {
            // Map grid field to entries field (e.g., WAL_CURR -> WAL, YIELD_CURR -> BOOK_YIELD)
            // This ensures we send properly named fields to the save endpoint
            const entriesFieldName = mapping.entriesField;
            saveRow[entriesFieldName] = changedFields[gridField];

            // Remove grid format field if it exists (shouldn't happen, but safety check)
            if (gridField !== entriesFieldName && gridField in saveRow) {
              delete saveRow[gridField];
            }
          } else {
            // Field doesn't need conversion (e.g., SECURITY, ISSUER_TRUST, NOTES, RISK_RATING, etc.)
            // Use as-is since grid and entries format use the same field name
            saveRow[gridField] = changedFields[gridField];
          }
        });

        // Ensure required fields are present and set STATUS
        saveRow.CUSIP = cusip;
        saveRow.MODIFIED_BY = userId;
        // The loaded token, unchanged; null for a CUSIP with no stored row yet.
        saveRow[MODIFIED_DATE_FIELD] = loadedModifiedDate(originalHolding);
        saveRow.ASOFDT = selectedDate;
        saveRow.STATUS = 'Active'; // Always set STATUS to Active for updated rows

        // Process row: exclude _PREV fields, grid format fields (_CURR), and other specified columns
        // Note: saveRow should be in entries format (WAL, BOOK_YIELD, etc.), not grid format
        const filteredRow = Object.keys(saveRow).reduce((acc, key) => {
          // Exclude all fields with _PREV suffix
          if (key.endsWith('_PREV')) {
            return acc;
          }

          // Exclude grid format fields (_CURR) - these should have been converted to entries format
          // This is a safety check in case conversion didn't happen
          if (key.endsWith('_CURR')) {
            console.warn(`WARNING: Grid format field ${key} found in saveRow. This should have been converted to entries format.`);
            return acc; // Skip grid format fields
          }

          // Exclude other specified columns
          if (columnsToExclude.includes(key)) {
            return acc;
          }

          // Keep all other fields as-is (should be in entries format: WAL, BOOK_YIELD, etc.)
          acc[key] = saveRow[key];

          return acc;
        }, {});

        // Convert empty numeric fields to null
        numericFields.forEach(field => {
          if (field in filteredRow && isEmpty(filteredRow[field])) {
            filteredRow[field] = null;
          }
        });

        // Ensure all required fields are present, setting to null if missing
        // This guarantees that all fields are included even if they don't have a value
        // from the holdings table or an update
        requiredFields.forEach(field => {
          if (!(field in filteredRow)) {
            // Field is missing, set to null
            filteredRow[field] = null;
          } else if (isEmpty(filteredRow[field])) {
            // Field exists but is empty
            if (numericFields.includes(field)) {
              // Numeric fields should be null if empty
              filteredRow[field] = null;
            } else if (field !== 'STATUS' && field !== MODIFIED_DATE_FIELD) {
              // Non-numeric fields (except STATUS which is always 'Active') should be null if empty
              filteredRow[field] = null;
            }
            // STATUS is already set to 'Active' above, so we don't override it
          }
        });

        return filteredRow;
      });


      await saveHoldings(completeUpdatedData);

      // Clear updated rows
      setUpdatedRows({});

      // Refresh data after save to get updated data
      await loadHoldings();

      // Show success message
      showNotification('Holdings saved successfully!', 'success');
    } catch (error) {
      console.error('Error saving holdings:', error);
      if (!notifyPermissionError(showNotification, error)) {
        // The backend's own text (conflict, bad request, outcome unknown).
        showNotification(getWriteErrorMessage(error, 'Failed to save holdings. Please try again.'), 'error');
      }
      if (shouldReloadAfterWriteError(error)) {
        // Someone else saved (or the save may have landed): reload, keeping
        // the unsaved edits visible so they can be re-applied.
        await loadHoldings({ keepEdits: updatedRows, adoptServerModifiedDates: isVersionConflict(error) });
      }
    } finally {
      setIsSaving(false);
    }
  }, [updatedRows, selectedDate, userId, loadHoldings, showNotification]);

  const handleExportToExcel = useCallback(() => {
    if (!gridRef.current || selectedRows.size === 0) {
      showNotification('No holdings selected for export', 'warning');
      return;
    }

    const currentDate = new Date().toISOString().split('T')[0];
    const selectedDateFormatted = selectedDate || 'unknown';

    // Select rows in AG Grid based on selectedRows Set
    gridRef.current.api.forEachNode((node) => {
      if (node.data && selectedRows.has(node.data.CUSIP)) {
        node.setSelected(true);
      }
    });

    const params = {
      fileName: `holdings_${selectedDateFormatted}_exported_${currentDate}.xlsx`,
      sheetName: 'Holdings',
      exportMode: 'xlsx',
      skipColumnHeaders: false,
      skipColumnGroupHeaders: false,
      skipPinnedTop: false,
      skipPinnedBottom: false,
      allColumns: false,
      onlySelected: true,
      columnKeys: [
        'CUSIP', 'SECURITY', 'ISSUER_TRUST', 'CW_ASSET_CLASS', 'TEAM', 'FINAL_RATING', 'BROAD_ASSET_CLASS',
        'SUBTOTAL_TAG', 'FINAL_GROUPING', 'BOOK_VALUE', 'WAL_CURR', 'WAL_PREV', 'YIELD_CURR',
        'YIELD_PREV', 'CE_CURR', 'CE_PREV', 'THICK_CURR', 'THICK_PREV', 'RISK_RATING',
        'CLASS_RATING', 'SPONSOR_MANAGER', 'NOTES', 'MODIFIED_BY', 'MODIFIED_DATE', 'ASOFDT'
      ]
    };

    gridRef.current.api.exportDataAsExcel(params);

    // Clear AG Grid selection after export
    gridRef.current.api.deselectAll();

    showNotification(`Excel file exported successfully (${selectedRows.size} row${selectedRows.size === 1 ? '' : 's'})`, 'success');
  }, [showNotification, selectedDate, selectedRows]);

  const handleUploadFileUpload = useCallback(async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      const parsedData = await parseFileData(file);

      if (!parsedData || parsedData.length === 0) {
        showNotification('File must contain at least one data row', 'error');
        return;
      }

      // Normalize headers using 1823-core utilities and pre-process values
      const normalizedRows = parsedData
        .filter(row => row && Object.keys(row).length > 0)
        .map(row => {
          const mapped = normalizeRowKeys(row, holdingsColumnNormalizer);

          // Process numeric fields
          ['WAL_CURR', 'YIELD_CURR', 'CE_CURR', 'THICK_CURR'].forEach(field => {
            if (mapped[field] !== undefined && mapped[field] !== null && mapped[field] !== '') {
              try {
                let cleanValue = String(mapped[field]).replace(/,/g, '').trim();
                if (field === 'YIELD_CURR' && cleanValue.includes('%')) {
                  cleanValue = cleanValue.replace(/%/g, '');
                }
                const numValue = Number(cleanValue);
                if (!isNaN(numValue) && isFinite(numValue)) {
                  mapped[field] = numValue;
                } else {
                  console.warn(`Invalid numeric value for ${field}:`, mapped[field]);
                  delete mapped[field];
                }
              } catch {
                console.warn(`Error processing numeric field ${field}:`, mapped[field]);
                delete mapped[field];
              }
            }
          });

          // String fields - trim whitespace
          ['SECURITY', 'ISSUER_TRUST', 'NOTES'].forEach(field => {
            if (mapped[field] !== undefined && mapped[field] !== null && mapped[field] !== '') {
              mapped[field] = String(mapped[field]).trim();
            }
          });

          // Ensure CUSIP is a trimmed string
          if (mapped.CUSIP !== undefined && mapped.CUSIP !== null && mapped.CUSIP !== '') {
            mapped.CUSIP = mapped.CUSIP.toString().trim();
          }

          return mapped;
        })
        .filter(row => row.CUSIP); // Skip rows without CUSIP

      // Track stats for the notification message
      const updatedCusips = new Set();
      let totalCellsChanged = 0;
      let totalCellsSkipped = 0;

      // Use 1823-core mergeRows with deduplication and custom onUpdateRow
      // that compares against originalHoldingsData (not current grid state)
      const numericFields = ['WAL_CURR', 'YIELD_CURR', 'CE_CURR', 'THICK_CURR'];
      const EPSILON = 0.001;

      // We need a custom onUpdateRow to compare against original data
      const onUpdateRow = (existingRow, uploadedRow) => {
        const cusip = existingRow.CUSIP;
        const originalHolding = originalHoldingsData.find(h => h.CUSIP === cusip);
        const merged = { ...existingRow };
        let hasChanges = false;
        const actualChanges = {};

        HOLDINGS_UPLOADABLE_FIELDS.forEach(field => {
          if (!(field in uploadedRow)) return;
          const uploadValue = uploadedRow[field];
          if (uploadValue === undefined || uploadValue === null || uploadValue === '') return;

          const originalValue = originalHolding?.[field];

          // Normalize for comparison
          const normalizeValue = (v) => (v === null || v === undefined || v === '') ? null : v;
          const normalizedOriginal = normalizeValue(originalValue);
          const normalizedNew = normalizeValue(uploadValue);

          let isDifferent = false;
          if (normalizedOriginal === null && normalizedNew === null) {
            isDifferent = false;
          } else if (normalizedOriginal === null || normalizedNew === null) {
            isDifferent = true;
          } else if (numericFields.includes(field)) {
            isDifferent = Math.abs(Number(normalizedOriginal) - Number(normalizedNew)) > EPSILON;
          } else {
            isDifferent = String(normalizedOriginal).trim() !== String(normalizedNew).trim();
          }

          if (isDifferent) {
            actualChanges[field] = uploadValue;
            hasChanges = true;
            totalCellsChanged++;
          } else {
            totalCellsSkipped++;
          }
        });

        if (hasChanges) {
          updatedCusips.add(cusip);
          actualChanges.STATUS = 'Active';

          // MODIFIED_DATE stays as loaded: a save sends the loaded HOLDINGS
          // row's token, never a value from the file.
          return {
            ...merged,
            ...actualChanges,
            MODIFIED_BY: userId,
            _actualChanges: actualChanges, // Stash for updatedRows tracking
          };
        }

        return merged;
      };

      const mergeResult = mergeRows({
        uploadedRows: normalizedRows,
        existingRows: holdingsData,
        matchField: 'CUSIP',
        mergeStrategy: 'upsert',
        deduplicateUpload: true,
        uploadableFields: HOLDINGS_UPLOADABLE_FIELDS,
        onUpdateRow,
      });

      // Apply merged data to state and extract tracking info
      if (updatedCusips.size > 0) {
        // Strip the _actualChanges stash and update tracking
        const cleanedData = mergeResult.mergedData.map(row => {
          if (row._actualChanges) {
            const { _actualChanges, ...rest } = row;
            return rest;
          }
          return row;
        });

        setHoldingsData(cleanedData);

        // Update change tracking
        const newUpdatedRows = {};
        mergeResult.mergedData.forEach(row => {
          if (row._actualChanges) {
            newUpdatedRows[row.CUSIP] = { ...row._actualChanges };
          }
        });
        setUpdatedRows(prev => ({ ...prev, ...newUpdatedRows }));
      }

      // Build detailed statistics message
      const uniqueRowsUpdated = updatedCusips.size;

      if (uniqueRowsUpdated === 0) {
        showNotification('No changes detected in uploaded file', 'warning');
      } else {
        let message = `Successfully updated ${uniqueRowsUpdated} unique row${uniqueRowsUpdated === 1 ? '' : 's'}`;
        message += ` with ${totalCellsChanged} cell${totalCellsChanged === 1 ? '' : 's'} overridden`;
        if (totalCellsSkipped > 0) {
          message += `. ${totalCellsSkipped} override${totalCellsSkipped === 1 ? ' was' : 's were'} skipped (already matched existing values)`;
        }
        message += '. Click Save to persist changes.';
        showNotification(message, 'success');
      }

    } catch (error) {
      console.error('Error processing holdings file:', error);
      showNotification(`Error processing file: ${error.message}`, 'error');
    }

    // Reset file input
    event.target.value = '';
  }, [userId, showNotification, originalHoldingsData, holdingsData]);

  // Row selection change handler for PamGrid
  const handleRowSelectionChange = useCallback((newSelectedRows) => {
    setSelectedRows(newSelectedRows);
  }, []);

  const holdingsColumnDefs = useMemo(() => [
    {
      headerName: "CUSIP",
      field: "CUSIP",
      pinned: 'left',
      minWidth: 120,
      flex: 1
    },
    {
      headerName: "Security",
      field: "SECURITY",
      pinned: 'left',
      minWidth: 200,
      flex: 2,
      editable: isGridEditable,
      cellEditor: 'agTextCellEditor',
      cellClass: getCellClass
    },
    {
      headerName: "Issuer Trust",
      field: "ISSUER_TRUST",
      minWidth: 150,
      flex: 1,
      editable: isGridEditable,
      cellEditor: 'agTextCellEditor',
      cellClass: getCellClass
    },
    {
      headerName: "CW Asset Class",
      field: "CW_ASSET_CLASS",
      minWidth: 120,
      flex: 1
    },
    {
      headerName: "Team",
      field: "TEAM",
      minWidth: 100,
      flex: 1
    },
    {
      headerName: "Final Rating",
      field: "FINAL_RATING",
      minWidth: 100,
      flex: 1
    },
    {
      headerName: "Broad Asset Class",
      field: "BROAD_ASSET_CLASS",
      minWidth: 150,
      flex: 1
    },
    {
      headerName: "Subtotal Tag",
      field: "SUBTOTAL_TAG",
      minWidth: 120,
      flex: 1
    },
    {
      headerName: "Final Grouping",
      field: "FINAL_GROUPING",
      minWidth: 120,
      flex: 1
    },
    {
      headerName: "Book Value",
      field: "BOOK_VALUE",
      type: 'numericColumn',
      minWidth: 120,
      flex: 1,
      valueFormatter: params => {
        if (!params.value) return '';
        return new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(params.value);
      }
    },
    {
      headerName: "WAL Curr",
      field: "WAL_CURR",
      type: 'numericColumn',
      minWidth: 100,
      flex: 1,
      editable: isGridEditable,
      cellEditor: 'agNumberCellEditor',
      cellEditorParams: {
        precision: 1,
        step: 0.1
      },
      cellClass: getCellClass,
      valueFormatter: params => {
        if (!params.value) return '';
        return new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 1,
          maximumFractionDigits: 1
        }).format(params.value);
      }
    },
    {
      headerName: "WAL Prev",
      field: "WAL_PREV",
      type: 'numericColumn',
      minWidth: 100,
      flex: 1,
      valueFormatter: params => {
        if (!params.value) return '';
        return new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 1,
          maximumFractionDigits: 1
        }).format(params.value);
      }
    },
    {
      headerName: "Yield Curr",
      field: "YIELD_CURR",
      type: 'numericColumn',
      minWidth: 100,
      flex: 1,
      editable: isGridEditable,
      cellEditor: 'agNumberCellEditor',
      cellEditorParams: {
        precision: 3,
        step: 0.001
      },
      cellClass: getCellClass,
      valueFormatter: params => {
        if (!params.value) return '';
        return new Intl.NumberFormat('en-US', {
          style: 'percent',
          minimumFractionDigits: 3,
          maximumFractionDigits: 3
        }).format(params.value / 100);
      }
    },
    {
      headerName: "Yield Prev",
      field: "YIELD_PREV",
      type: 'numericColumn',
      minWidth: 100,
      flex: 1,
      valueFormatter: params => {
        if (!params.value) return '';
        return new Intl.NumberFormat('en-US', {
          style: 'percent',
          minimumFractionDigits: 3,
          maximumFractionDigits: 3
        }).format(params.value / 100);
      }
    },
    {
      headerName: "CE Curr",
      field: "CE_CURR",
      type: 'numericColumn',
      minWidth: 100,
      flex: 1,
      editable: isGridEditable,
      cellEditor: 'agNumberCellEditor',
      cellEditorParams: {
        precision: 2,
        step: 0.01
      },
      cellClass: getCellClass,
      valueFormatter: params => {
        if (params.value == null || params.value === '') return '';
        return new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(params.value);
      },
      valueParser: params => {
        if (params.newValue === '' || params.newValue == null) return null;
        // Remove commas and parse as number
        const cleaned = params.newValue.toString().replace(/,/g, '');
        const parsed = Number(cleaned);
        return isNaN(parsed) ? null : parsed;
      }
    },
    {
      headerName: "CE Prev",
      field: "CE_PREV",
      type: 'numericColumn',
      minWidth: 100,
      flex: 1,
      valueFormatter: params => {
        if (!params.value) return '';
        return new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(params.value);
      }
    },
    {
      headerName: "Thick Curr",
      field: "THICK_CURR",
      type: 'numericColumn',
      minWidth: 100,
      flex: 1,
      editable: isGridEditable,
      cellEditor: 'agNumberCellEditor',
      cellEditorParams: {
        precision: 2,
        step: 0.01
      },
      cellClass: getCellClass,
      valueFormatter: params => {
        if (params.value == null || params.value === '') return '';
        return new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(params.value);
      },
      valueParser: params => {
        if (params.newValue === '' || params.newValue == null) return null;
        // Remove commas and parse as number
        const cleaned = params.newValue.toString().replace(/,/g, '');
        const parsed = Number(cleaned);
        return isNaN(parsed) ? null : parsed;
      }
    },
    {
      headerName: "Thick Prev",
      field: "THICK_PREV",
      type: 'numericColumn',
      minWidth: 100,
      flex: 1,
      valueFormatter: params => {
        if (!params.value) return '';
        return new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(params.value);
      }
    },
    {
      headerName: "Risk Rating",
      field: "RISK_RATING",
      type: 'numericColumn',
      minWidth: 110,
      flex: 1,
      editable: isGridEditable,
      cellEditor: 'agNumberCellEditor',
      cellEditorParams: {
        precision: 0,
        step: 1,
        min: 1,
        max: 4
      },
      cellClass: getCellClass,
      valueFormatter: params => {
        if (params.value == null || params.value === '') return '';
        return Math.round(params.value).toString();
      },
      valueParser: params => {
        if (params.newValue === '' || params.newValue == null) return null;
        const parsed = parseInt(params.newValue, 10);
        if (isNaN(parsed)) return null;
        // Clamp between 1 and 4
        return Math.max(1, Math.min(4, parsed));
      }
    },
    {
      headerName: "Class Rating",
      field: "CLASS_RATING",
      minWidth: 120,
      flex: 1,
      editable: isGridEditable,
      cellEditor: 'agTextCellEditor',
      cellClass: getCellClass
    },
    {
      headerName: "Sponsor Manager",
      field: "SPONSOR_MANAGER",
      minWidth: 150,
      flex: 1,
      editable: isGridEditable,
      cellEditor: 'agTextCellEditor',
      cellClass: getCellClass
    },
    {
      headerName: "Notes",
      field: "NOTES",
      minWidth: 120,
      flex: 1,
      editable: isGridEditable,
      cellEditor: 'agTextCellEditor',
      cellClass: getCellClass
    },
    {
      headerName: "Modified By",
      field: "MODIFIED_BY",
      minWidth: 120,
      flex: 1,
      editable: false,
      cellClass: getCellClass
    },
    {
      headerName: "Modified Date",
      field: "MODIFIED_DATE",
      minWidth: 120,
      flex: 1,
      editable: false,
      cellClass: getCellClass,
      valueFormatter: params => {
        if (!params.value) return '';
        try {
          const date = new Date(params.value);
          return date.toLocaleString();
        } catch (error) {
          return params.value;
        }
      }
    }
  ], [getCellClass, isGridEditable]);

  const defaultColDef = useMemo(() => ({
    sortable: true,
    filter: true,
    resizable: true,
    minWidth: 120, // Increased minimum width to account for header text
    flex: 1,
    cellStyle: {
      color: theme.palette.text.primary,
      backgroundColor: theme.palette.background.paper,
      borderColor: theme.palette.divider
    },
    lockPinned: false
  }), [theme]);

  // Custom toolbar buttons for PamGrid
  const customToolbarButtons = useMemo(() => {
    const buttons = [
      {
        label: showOnlyChanges ? 'Show All Rows' : 'Show Changes Only',
        onClick: () => setShowOnlyChanges(!showOnlyChanges),
        disabled: isLoading,
        sx: {
          backgroundColor: showOnlyChanges ? 'orange' : 'lightblue',
          color: 'black',
          '&:hover': {
            backgroundColor: showOnlyChanges ? 'rgba(255, 165, 0, 0.8)' : 'rgba(173, 216, 230, 0.8)'
          }
        },
        tooltip: showOnlyChanges ? 'Show all rows' : 'Show only rows with month-over-month changes'
      }
    ];

    return buttons;
  }, [showOnlyChanges, isLoading]);

  // Own the grid-config hook here so we can bridge gridConfigs.onGridReady
  // to PamGrid — without that bridge, a slow grid that mounts AFTER the
  // configs response would miss the default-layout auto-apply. See
  // useGridConfigs.js for the grid-ready race rationale.
  const gridConfigs = useGridConfigs(api, {
    app: 'blotter',
    gridId: 'holdings',
    gridApiRef: gridRef,
  });

  const gridConfigComponents = useMemo(() => [
    <GridConfigMenu key="grid-config-menu" controller={gridConfigs} />,
  ], [gridConfigs]);

  // Compose the existing column-autosize onGridReady with the grid-config
  // default-layout bridge so both fire when the grid becomes ready.
  const handleGridReadyComposed = useCallback((...args) => {
    handleGridReady(...args);
    gridConfigs.onGridReady(...args);
  }, [handleGridReady, gridConfigs]);

  // Legend items for PamGrid
  const legendItems = useMemo(() => [
    {
      color: 'rgba(255, 165, 0, 0.2)',
      border: '1px solid rgba(255, 165, 0, 0.4)',
      label: 'Orange: User-edited fields'
    },
    {
      color: 'rgba(255, 0, 0, 0.2)',
      border: '1px solid rgba(255, 0, 0, 0.4)',
      label: 'Red: Month-over-month changes'
    }
  ], []);

  // Custom instructions for Holdings
  const holdingsInstructions = useMemo(() => (
    <>
      <strong>Navigation & Copy/Paste:</strong> Click to select cells, use arrow keys to navigate, Shift+arrow keys to select ranges, Ctrl+C to copy, Ctrl+V to paste, Ctrl+X to cut.
      <br />
      <strong>Month Comparison Rules:</strong> Text fields highlight when values differ from previous month. WAL highlights when difference ≥ 0.5. Yield highlights when difference ≥ 1.0.
    </>
  ), []);

  // Handle upload for PamGrid toolbar
  const handleUploadFile = useCallback((file) => {
    // Create a synthetic event to reuse existing handler
    const syntheticEvent = { target: { files: [file], value: '' } };
    handleUploadFileUpload(syntheticEvent);
  }, [handleUploadFileUpload]);

  // Handle date change for PamGrid toolbar
  const handlePamGridDateChange = useCallback((newDate) => {
    setSelectedDate(newDate);
  }, []);

  return (
    <Box>
      {/* Error display */}
      {holdingsError && (
        <Typography color="error" sx={{ mb: 2 }}>
          {holdingsError}
        </Typography>
      )}

      <PamGrid
        ref={gridRef}
        // Data
        rowData={filteredHoldingsData}
        columnDefs={holdingsColumnDefs}

        // Toolbar configuration
        title="Holdings"
        showToolbar={true}
        showUpload={true}
        onUpload={handleUploadFile}
        uploadLabel="Upload Holdings"
        uploadTooltip="Upload Excel or CSV file with holdings data"

        showBulkDelete={true}
        onBulkDelete={handleBulkDelete}
        selectedRowCount={selectedRows.size}

        showExport={true}
        onExport={handleExportToExcel}
        exportLabel={selectedRows.size > 0 ? `Export Selected (${selectedRows.size})` : 'Export Selected'}

        showSave={true}
        onSave={handleSaveHoldings}
        changedRowCount={Object.keys(updatedRows).length}
        isSaving={isSaving}

        showDateSelect={true}
        availableDates={availableDates}
        selectedDate={selectedDate}
        onDateChange={handlePamGridDateChange}
        isLoadingDates={isLoadingDates}

        customToolbarButtons={customToolbarButtons}
        customToolbarComponents={gridConfigComponents}

        // Quick filter
        showQuickFilter={true}
        quickFilterText={holdingsQuickFilter}
        onQuickFilterChange={setHoldingsQuickFilter}
        quickFilterPlaceholder="Quick Filter Holdings..."

        // Sizing
        height="calc(100vh - 380px)"
        rowHeight={32}

        // Selection column
        showSelectionColumn={true}
        selectedRows={selectedRows}
        onRowSelectionChange={handleRowSelectionChange}
        getRowId={(data) => data.CUSIP}
        isSelectionDisabled={areActionsDisabled}

        // Actions column
        showActions={true}
        actions={['history', 'delete']}
        onHistory={handleHoldingHistory}
        onDelete={handleDeleteHolding}
        isActionDisabled={() => areActionsDisabled}

        // Default column definition with custom cell class
        defaultColDef={defaultColDef}

        // State
        isLoading={isLoading || isSaving || isDeleting}
        loadingMessage={isLoading ? 'Loading holdings data...' : isSaving ? 'Saving holdings data...' : isDeleting ? 'Deleting holdings...' : 'Processing...'}

        // Callbacks
        onCellValueChanged={isGridEditable && !isLoading && !isSaving && !isDeleting ? handleCellValueChanged : undefined}
        onGridReady={handleGridReadyComposed}
        onFilterChanged={gridConfigs.onGridStateChanged}
        agGridProps={gridConfigs.stashBindings}
        onCellKeyDown={onCellKeyDown}
        onCopy={handleCopy}
        allowPaste={isGridEditable && !isLoading && !isSaving && !isDeleting}
        onPasteStart={isGridEditable && !isLoading && !isSaving && !isDeleting ? onPasteStart : undefined}
        onPasteEnd={isGridEditable && !isLoading && !isSaving && !isDeleting ? onPasteEnd : undefined}

        // Instructions and Legend
        showInstructions={true}
        instructions={holdingsInstructions}
        showLegend={true}
        legendItems={legendItems}
      />

      {/* Holdings History Modal */}
      <HistoryModal
        open={historyModalOpen}
        onClose={() => setHistoryModalOpen(false)}
        title={`Holding History - ${selectedHoldingForHistory?.SECURITY || selectedHoldingForHistory?.CUSIP || 'Holding'}`}
      >
        <HoldingsHistory cusip={selectedHoldingForHistory?.CUSIP} asofdt={selectedDate} showTitle={false} />
      </HistoryModal>

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleConfirmDelete}
        title="Delete Holding"
        message={
          holdingToDelete
            ? `Are you sure you want to delete the holding "${holdingToDelete.Security || holdingToDelete.CUSIP}"?\n\nThis action cannot be undone.`
            : "Are you sure you want to delete this holding?"
        }
        confirmText="Delete"
        cancelText="Cancel"
        confirmColor="error"
      />

      {/* Bulk Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={bulkDeleteDialogOpen}
        onClose={handleCloseBulkDeleteDialog}
        onConfirm={handleConfirmBulkDelete}
        title="Delete Selected Holdings"
        message={
          selectedRows.size > 0
            ? `Are you sure you want to delete ${selectedRows.size} selected holding${selectedRows.size === 1 ? '' : 's'}?\n\nThis action cannot be undone.`
            : "No holdings selected for deletion."
        }
        confirmText="Delete All"
        cancelText="Cancel"
        confirmColor="error"
      />

    </Box>
  );
};

export default Holdings;
